import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    AlertTriangle, ArrowLeft, Building2, Calendar, CheckCircle2, ChevronRight, Globe, History, IndianRupee, Loader2, Lock,
    MessageSquare, MessageSquareText, Percent, ReceiptText, RotateCcw, Save, Search, Shield, Unlock, Upload, Users, WalletCards, X,
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { SMIIFY_API, API_V1 } from '../../config/api';
import styles from './Settings.module.css';
import LogoUploader from './components/LogoUploader/LogoUploader';
import WhatsAppMessages from './WhatsAppMessages/WhatsAppMessages';

const BRANCH_API = `${SMIIFY_API}/settings`;
const HQ_API = `${API_V1}/smiify/hq/settings`;
const ICONS = { building: Building2, percent: Percent, receipt: ReceiptText, rupee: IndianRupee, wallet: WalletCards, calendar: Calendar, users: Users, message: MessageSquare, shield: Shield, globe: Globe };
const MASK = '••••••••';

// WhatsApp messages: which template goes out for each moment (its own editor, its own save)
//   link straight to it: /settings?section=whatsapp-messages&msg=bill
const WA_SECTION = 'whatsapp-messages';
const WA_WORDS = ['whatsapp', 'message', 'template', 'bill', 'invoice', 'appointment', 'booking', 'reminder', 'feedback', 'membership', 'package', 'wallet', 'balance', 'closing', 'eod', 'staff', 'sms', 'notification'];

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const show = (fd, v) => {
    if (fd.type === 'boolean') return v ? 'On' : 'Off';
    if (fd.type === 'select') return fd.options?.find((o) => String(o.value) === String(v))?.label ?? String(v);
    if (fd.type === 'secret') return v ? 'Set' : 'Empty';
    if (fd.type === 'image') return v ? 'Image' : 'None';
    return v === '' || v === null || v === undefined ? 'Empty' : String(v);
};

export default function Settings() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { activeStore } = useStore();
    const { refreshAccess } = useAuth();
    const token = localStorage.getItem('smiify_token');
    const storeId = activeStore?._id || activeStore?.id;
    const isHqStore = Boolean(activeStore?.isHQ || storeId === 'global_hq');
    const branchParam = params.get('branch');
    const mode = isHqStore ? (branchParam ? 'HQ_BRANCH' : 'HQ') : 'BRANCH';
    // WhatsApp messages belong to the branch (or HQ) that is selected, not to a branch opened from HQ
    const showWa = mode !== 'HQ_BRANCH';

    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [secretDraft, setSecretDraft] = useState({});
    const [resets, setResets] = useState([]);
    const [policyDraft, setPolicyDraft] = useState({});
    const [tab, setTab] = useState('settings');
    const [section, setSection] = useState(params.get('section') || '');
    const [search, setSearch] = useState('');
    const [showBranchOnly, setShowBranchOnly] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [waSummary, setWaSummary] = useState(null);       // { auto, total } from the WhatsApp editor

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(mode === 'BRANCH' && storeId ? { 'x-store-id': String(storeId) } : {}),
    }), [token, mode, storeId]);

    const url = mode === 'BRANCH' ? `${BRANCH_API}?storeId=${storeId}` : mode === 'HQ' ? HQ_API : `${HQ_API}/branches/${branchParam}`;
    const saveUrl = mode === 'BRANCH' ? BRANCH_API : mode === 'HQ' ? HQ_API : `${HQ_API}/branches/${branchParam}`;

    const applyData = useCallback((d) => {
        setData(d);
        setDraft(d.settings.values);
        setSecretDraft({});
        setResets([]);
        setPolicyDraft({});
        setFieldErrors({});
        setSection((cur) => (cur === WA_SECTION || (cur && d.schema.sections.some((s) => s.id === cur)) ? cur : d.schema.sections[0]?.id || ''));
    }, []);

    const load = useCallback(async () => {
        if (!storeId) return;
        setLoading(true);
        try {
            const res = await fetch(url, { headers });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not load settings.');
            applyData(json.data);
        } catch (e) {
            setMsg({ type: 'error', text: e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message });
        } finally {
            setLoading(false);
        }
    }, [url, headers, storeId, applyData]);

    useEffect(() => { load(); setTab('settings'); }, [load]);

    // a branch opened from HQ has no WhatsApp section: show its first settings section instead
    useEffect(() => {
        if (!showWa && section === WA_SECTION && data) setSection(data.schema.sections[0]?.id || '');
    }, [showWa, section, data]);

    const openSection = (id) => {
        setSection(id);
        setSearch('');
        // keep the address in step, so the page can be shared or reloaded on the same section
        const next = new URLSearchParams(params);
        if (id === WA_SECTION) next.set('section', WA_SECTION); else next.delete('section');
        next.delete('msg');
        setParams(next, { replace: true });
    };

    /* ----------------------------- derived ----------------------------- */
    const fields = useMemo(() => data?.schema?.fields || [], [data]);
    const fieldMap = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields]);
    const meta = data?.settings?.meta || {};
    const policy = { ...(data?.settings?.policy || {}), ...Object.fromEntries(Object.entries(policyDraft).map(([k, v]) => [k, v === 'LOCKED' ? 'LOCKED' : undefined])) };

    const changes = useMemo(() => {
        if (!data) return {};
        const out = {};
        fields.forEach((f) => {
            if (f.type === 'secret') { if (secretDraft[f.key]) out[f.key] = secretDraft[f.key]; return; }
            if (!same(draft[f.key], data.settings.values[f.key])) out[f.key] = draft[f.key];
        });
        return out;
    }, [data, draft, secretDraft, fields]);
    const policyChanges = Object.entries(policyDraft).filter(([k, v]) => (data?.settings?.policy?.[k] === 'LOCKED') !== (v === 'LOCKED'));
    const dirtyCount = Object.keys(changes).length + resets.length + policyChanges.length;

    useEffect(() => {
        const warn = (e) => { if (dirtyCount) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirtyCount]);

    const visible = (f) => {
        if (mode === 'HQ' && f.scope === 'BRANCH' && !showBranchOnly) return false;
        // showIf: ['key', value]  or several conditions that must all hold: [['key', value], ['key2', value2]]
        if (f.showIf) {
            const conds = Array.isArray(f.showIf[0]) ? f.showIf : [f.showIf];
            return conds.every(([key, value]) => same(draft[key], value));
        }
        return true;
    };

    const sectionErrors = useMemo(() => {
        const m = {};
        Object.keys(fieldErrors).forEach((k) => { const sec = fieldMap[k]?.section; if (sec) m[sec] = (m[sec] || 0) + 1; });
        return m;
    }, [fieldErrors, fieldMap]);

    /* ----------------------------- edit ----------------------------- */
    const setValue = (key, v) => {
        setDraft((d) => ({ ...d, [key]: v }));
        setResets((r) => r.filter((x) => x !== key));
        setFieldErrors((e) => { if (!e[key]) return e; const n = { ...e }; delete n[key]; return n; });
    };

    const resetToHq = (key) => {
        setResets((r) => [...new Set([...r, key])]);
        setDraft((d) => ({ ...d, [key]: data.settings.values[key] }));
    };

    const toggleLock = (key) => {
        const now = policy[key] === 'LOCKED';
        setPolicyDraft((p) => ({ ...p, [key]: now ? 'DEFAULT' : 'LOCKED' }));
    };

    const discard = () => {
        setDraft(data.settings.values);
        setSecretDraft({});
        setResets([]);
        setPolicyDraft({});
        setFieldErrors({});
    };

    /* ----------------------------- save ----------------------------- */
    const send = async (body) => {
        setSaving(true);
        setMsg(null);
        try {
            const res = await fetch(saveUrl, { method: 'PATCH', headers, body: JSON.stringify(body) });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                if (json.fieldErrors) {
                    setFieldErrors(json.fieldErrors);
                    const first = Object.keys(json.fieldErrors)[0];
                    if (fieldMap[first]) { setSection(fieldMap[first].section); setSearch(''); setTab('settings'); }
                }
                throw new Error(json.message || 'Could not save.');
            }
            applyData(json.data);
            setMsg({ type: 'ok', text: json.message || 'Saved.' });
            setConfirm(null);
            if (mode === 'BRANCH') refreshAccess?.();
        } catch (e) {
            setMsg({ type: 'error', text: e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message });
        } finally {
            setSaving(false);
        }
    };

    const onSave = () => {
        if (!dirtyCount) return;
        if (mode === 'HQ') {
            const changedShared = Object.keys(changes).filter((k) => fieldMap[k]?.scope === 'BOTH');
            const affected = (data.branches || []).filter((b) => b.changedKeys.some((k) => changedShared.includes(k)));
            setConfirm({ changedShared, affected, reset: false });
            return;
        }
        send({ ...(mode === 'BRANCH' ? { storeId } : {}), changes, reset: resets });
    };

    const confirmHqSave = () => send({
        changes,
        policy: Object.fromEntries(policyChanges),
        ...(confirm.reset && confirm.changedShared.length ? { resetBranches: { keys: confirm.changedShared, stores: confirm.affected.map((b) => b.storeId) } } : {}),
    });

    const resetBranch = (b) => {
        if (!window.confirm(`Bring ${b.changedKeys.length} setting(s) of ${b.name} back to the HQ value?`)) return;
        send({ resetBranches: { keys: b.changedKeys, stores: [b.storeId] } });
    };

    /* ----------------------------- render helpers ----------------------------- */
    const badge = (f) => {
        const m = meta[f.key] || {};
        if (mode === 'HQ') {
            if (f.scope === 'BRANCH') return <span className={styles.bMuted}>Each branch sets this</span>;
            if (f.scope === 'HQ') return <span className={styles.bHq}>Brand rule</span>;
            return null;
        }
        if (m.source === 'HQ_LOCKED') return <span className={styles.bLock}><Lock size={10} /> Locked by HQ</span>;
        if (m.source === 'HQ_SHARED') return <span className={styles.bHq}>Using HQ account</span>;
        if (m.source === 'HQ_ONLY') return <span className={styles.bHq}>Set by HQ</span>;
        if (resets.includes(f.key)) return <span className={styles.bHq}>Will use HQ value</span>;
        if (m.source === 'BRANCH' && f.scope === 'BOTH') return <span className={styles.bOwn}>Changed here</span>;
        if (m.source === 'HQ_DEFAULT') return <span className={styles.bHq}>From HQ</span>;
        if (f.reason === 'NO_ACCESS') return <span className={styles.bMuted}><Lock size={10} /> No access</span>;
        return null;
    };

    const renderInput = (f) => {
        const v = draft[f.key];
        const disabled = !f.editable || saving;
        const err = fieldErrors[f.key];
        const cls = `${styles.input} ${err ? styles.inputErr : ''}`;
        switch (f.type) {
            case 'boolean':
                return (
                    <label className={styles.switch}>
                        <input type="checkbox" checked={Boolean(v)} disabled={disabled} onChange={(e) => setValue(f.key, e.target.checked)} aria-label={f.label} />
                        <span />
                    </label>
                );
            case 'select':
                return (
                    <select className={cls} value={String(v ?? '')} disabled={disabled} onChange={(e) => {
                        const o = f.options.find((x) => String(x.value) === e.target.value);
                        setValue(f.key, o ? o.value : e.target.value);
                    }} aria-label={f.label}>
                        {f.options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
                    </select>
                );
            case 'number':
                return <input className={cls} type="number" min={f.min} max={f.max} value={v ?? ''} disabled={disabled} onChange={(e) => setValue(f.key, e.target.value === '' ? '' : Number(e.target.value))} aria-label={f.label} />;
            case 'time':
                return <input className={cls} type="time" value={v || ''} disabled={disabled} onChange={(e) => setValue(f.key, e.target.value)} aria-label={f.label} />;
            case 'textarea':
                return <textarea className={cls} rows={3} maxLength={f.maxLength} value={v || ''} disabled={disabled} onChange={(e) => setValue(f.key, e.target.value)} aria-label={f.label} />;
            case 'secret': {
                const s = data.settings.secrets?.[f.key] || {};
                return (
                    <input
                        className={cls}
                        type="password"
                        autoComplete="new-password"
                        value={secretDraft[f.key] || ''}
                        placeholder={s.hasValue ? `Saved${s.hint ? ` (${s.hint})` : ''}. Type to replace.` : 'Not set'}
                        disabled={disabled}
                        onChange={(e) => setSecretDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                        aria-label={f.label}
                    />
                );
            }
            case 'image':
                return (
                    <div className={styles.imageField}>
                        <div className={styles.imagePreview}>{v && v !== MASK ? <img src={v} alt="" /> : <Upload size={16} />}</div>
                        {!disabled && (
                            <div className={styles.imageActions}>
                                <label className={styles.btnSmall}>
                                    <Upload size={11} /> {v ? 'Replace' : 'Upload'}
                                    <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        if (file.size > 400 * 1024) { setFieldErrors((x) => ({ ...x, [f.key]: 'Image must be under 400 KB.' })); return; }
                                        const r = new FileReader();
                                        r.onload = () => setValue(f.key, r.result);
                                        r.readAsDataURL(file);
                                    }} />
                                </label>
                                {v && <button type="button" className={styles.btnSmall} onClick={() => setValue(f.key, '')}><X size={11} /> Remove</button>}
                            </div>
                        )}
                    </div>
                );
            case 'phones':
            case 'text':
            default:
                return (
                    <input
                        className={cls}
                        value={v === MASK ? '' : (v ?? '')}
                        placeholder={v === MASK ? 'Hidden' : (f.type === 'phones' ? '9876543210, 9812345678' : '')}
                        maxLength={f.maxLength}
                        disabled={disabled || v === MASK}
                        onChange={(e) => setValue(f.key, e.target.value)}
                        aria-label={f.label}
                    />
                );
        }
    };

    const renderField = (f) => {
        const m = meta[f.key] || {};
        const isBool = f.type === 'boolean';
        const lockable = mode === 'HQ' && f.lockable;
        const locked = policy[f.key] === 'LOCKED';
        const canReset = mode !== 'HQ' && f.editable && f.scope === 'BOTH' && m.source === 'BRANCH' && !resets.includes(f.key);
        const changed = Object.prototype.hasOwnProperty.call(changes, f.key) || resets.includes(f.key);
        return (
            <div key={f.key} className={`${styles.field} ${f.wide || f.type === 'textarea' || f.type === 'image' ? styles.wide : ''} ${isBool ? styles.boolField : ''} ${changed ? styles.changed : ''} ${!f.editable ? styles.readOnly : ''}`} id={`f-${f.key}`}>
                <div className={styles.fieldHead}>
                    <label className={styles.label}>{f.label}{f.required && <em>*</em>}</label>
                    <div className={styles.badges}>
                        {badge(f)}
                        {lockable && (
                            <button type="button" className={`${styles.lockBtn} ${locked ? styles.lockOn : ''}`} onClick={() => toggleLock(f.key)} title={locked ? 'Locked: branches cannot change this' : 'Branches may change this'}>
                                {locked ? <Lock size={11} /> : <Unlock size={11} />} {locked ? 'Locked' : 'Lock'}
                            </button>
                        )}
                    </div>
                </div>
                {isBool ? (
                    <div className={styles.boolRow}>
                        {f.help ? <small>{f.help}</small> : <span />}
                        {renderInput(f)}
                    </div>
                ) : (
                    <>
                        {renderInput(f)}
                        {f.help && <small className={styles.help}>{f.help}</small>}
                    </>
                )}
                {canReset && (
                    <button type="button" className={styles.resetLink} onClick={() => resetToHq(f.key)}>
                        <RotateCcw size={10} /> Use HQ value ({show(f, m.hqValue)})
                    </button>
                )}
                {fieldErrors[f.key] && <span className={styles.err}>{fieldErrors[f.key]}</span>}
            </div>
        );
    };

    const renderSectionFields = (secId, list) => {
        const groups = [];
        list.forEach((f) => {
            const g = f.group || '';
            let bucket = groups.find((x) => x.name === g);
            if (!bucket) { bucket = { name: g, items: [] }; groups.push(bucket); }
            bucket.items.push(f);
        });
        return groups.map((g) => (
            <div key={`${secId}-${g.name}`}>
                {g.name && <h4 className={styles.groupHead}>{g.name}</h4>}
                <div className={styles.grid}>{g.items.map(renderField)}</div>
            </div>
        ));
    };

    /* ----------------------------- page ----------------------------- */
    if (!storeId) return <div className={styles.center}>Select a branch first.</div>;
    if (loading && !data) return <div className={styles.center}><Loader2 size={18} className={styles.spin} /> Loading settings...</div>;
    if (!data) return <div className={styles.center}><AlertTriangle size={18} /> {msg?.text || 'Settings not available.'}</div>;

    const sections = data.schema.sections;
    const q = search.trim().toLowerCase();
    const searchHits = q ? fields.filter((f) => visible(f) && `${f.label} ${f.help}`.toLowerCase().includes(q)) : [];
    const waHit = showWa && q.length >= 2 && WA_WORDS.some((w) => w.startsWith(q) || q.includes(w));
    const onWa = showWa && section === WA_SECTION;
    const secFields = fields.filter((f) => f.section === section && visible(f));
    const sec = sections.find((s) => s.id === section);
    const lockedIn = (id) => fields.filter((f) => f.section === id && (mode === 'HQ' ? policy[f.key] === 'LOCKED' : meta[f.key]?.locked)).length;
    const title = mode === 'HQ' ? 'Brand settings (HQ)' : mode === 'HQ_BRANCH' ? `Branch settings: ${data.profile?.businessName || 'Branch'}` : 'Salon settings';

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headLeft}>
                    <button type="button" className={styles.iconBtn} onClick={() => (mode === 'HQ_BRANCH' ? navigate('/settings') : navigate(-1))} aria-label="Back"><ArrowLeft size={15} /></button>
                    <div>
                        <h1>{title}</h1>
                        <p>
                            {mode === 'HQ' && 'Values here are the default for every branch. Lock a setting to enforce it.'}
                            {mode === 'HQ_BRANCH' && 'You are changing this branch directly from HQ.'}
                            {mode === 'BRANCH' && (data.hq?.connected
                                ? `${activeStore?.storeName || 'This branch'} follows ${data.hq.brandName || 'HQ'} settings${data.hq.lockedCount ? `, ${data.hq.lockedCount} locked by HQ` : ''}.`
                                : `Settings for ${activeStore?.storeName || 'this branch'}.`)}
                        </p>
                    </div>
                </div>
                <div className={styles.tabs} role="tablist">
                    <button type="button" role="tab" aria-selected={tab === 'settings'} className={tab === 'settings' ? styles.tabOn : ''} onClick={() => setTab('settings')}>Settings</button>
                    {mode === 'HQ' && <button type="button" role="tab" aria-selected={tab === 'branches'} className={tab === 'branches' ? styles.tabOn : ''} onClick={() => setTab('branches')}>Branches</button>}
                    {mode === 'HQ' && <button type="button" role="tab" onClick={() => navigate('/global-access')}>Access limits</button>}
                    {data.permissions?.canSeeHistory && <button type="button" role="tab" aria-selected={tab === 'history'} className={tab === 'history' ? styles.tabOn : ''} onClick={() => setTab('history')}><History size={12} /> History</button>}
                </div>
            </header>

            {msg && (
                <div className={`${styles.msg} ${msg.type === 'ok' ? styles.msgOk : styles.msgErr}`} role="status">
                    {msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} <span>{msg.text}</span>
                    <button type="button" onClick={() => setMsg(null)} aria-label="Close"><X size={12} /></button>
                </div>
            )}

            {tab === 'settings' && (
                <div className={styles.layout}>
                    <aside className={styles.side}>
                        <div className={styles.search}>
                            <Search size={13} />
                            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search settings..." aria-label="Search settings" />
                            {search && <button type="button" onClick={() => setSearch('')} aria-label="Clear"><X size={12} /></button>}
                        </div>
                        <nav className={styles.nav}>
                            {sections.map((s) => {
                                const Icon = ICONS[s.icon] || Building2;
                                const locks = lockedIn(s.id);
                                return (
                                    <button type="button" key={s.id} className={`${styles.navItem} ${section === s.id && !q ? styles.navOn : ''}`} onClick={() => openSection(s.id)}>
                                        <span className={styles.navIcon}><Icon size={13} /></span>
                                        <span className={styles.navText}>
                                            <strong>{s.title}</strong>
                                            <small>{!s.editable ? 'View only' : locks ? `${locks} locked` : s.desc.split('.')[0]}</small>
                                        </span>
                                        {sectionErrors[s.id] && <i className={styles.navErr}>{sectionErrors[s.id]}</i>}
                                    </button>
                                );
                            })}
                            {showWa && (
                                <button type="button" className={`${styles.navItem} ${onWa && !q ? styles.navOn : ''}`} onClick={() => openSection(WA_SECTION)}>
                                    <span className={styles.navIcon}><MessageSquareText size={13} /></span>
                                    <span className={styles.navText}>
                                        <strong>WhatsApp messages</strong>
                                        <small>{waSummary ? `${waSummary.auto} of ${waSummary.total} sent automatically` : 'Template for bills, bookings, closing'}</small>
                                    </span>
                                </button>
                            )}
                        </nav>
                        {mode === 'HQ' && (
                            <label className={styles.sideToggle}>
                                <input type="checkbox" checked={showBranchOnly} onChange={(e) => setShowBranchOnly(e.target.checked)} />
                                Show branch-only settings
                            </label>
                        )}
                    </aside>

                    <main className={styles.main}>
                        {q ? (
                            <>
                                <div className={styles.secHead}><h2>Search</h2><p>{searchHits.length + (waHit ? 1 : 0)} setting(s) match "{search}"</p></div>
                                {waHit && (
                                    <section className={styles.card}>
                                        <button type="button" className={styles.navItem} style={{ width: '100%' }} onClick={() => openSection(WA_SECTION)}>
                                            <span className={styles.navIcon}><MessageSquareText size={13} /></span>
                                            <span className={styles.navText}>
                                                <strong>WhatsApp messages</strong>
                                                <small>Which template goes out for day closing, bills, feedback, appointments, memberships, packages and balance.</small>
                                            </span>
                                            <ChevronRight size={14} />
                                        </button>
                                    </section>
                                )}
                                {sections.map((s) => {
                                    const hits = searchHits.filter((f) => f.section === s.id);
                                    if (!hits.length) return null;
                                    return <section key={s.id} className={styles.card}><h3 className={styles.cardTitle}>{s.title}</h3>{renderSectionFields(s.id, hits)}</section>;
                                })}
                            </>
                        ) : onWa ? (
                            // its own editor and its own Save (one message at a time)
                            <WhatsAppMessages embedded initialEvent={params.get('msg') || ''} onSummary={setWaSummary} />
                        ) : sec && (
                            <>
                                <div className={styles.secHead}>
                                    <h2>{sec.title}</h2>
                                    <p>{sec.desc}</p>
                                </div>
                                {!sec.editable && <div className={styles.info}><Lock size={12} /> You can view these settings. {mode === 'BRANCH' ? 'Ask an admin for access, or HQ if they are locked.' : ''}</div>}
                                {mode === 'BRANCH' && sec.id === 'profile' && (
                                    <LogoUploader
                                        storeId={storeId}
                                        logoUrl={data?.branding?.logo || ''}
                                        canEdit={Boolean(sec.editable)}
                                        onChange={(logo) => setData((d) => ({ ...d, branding: { ...(d?.branding || {}), logo } }))}
                                    />
                                )}
                                <section className={styles.card}>{renderSectionFields(sec.id, secFields)}</section>
                            </>
                        )}
                    </main>
                </div>
            )}

            {tab === 'branches' && mode === 'HQ' && (
                <div className={styles.pad}>
                    <section className={styles.card}>
                        <table className={styles.table}>
                            <thead><tr><th>Branch</th><th>Changed from HQ</th><th>Ignored (locked)</th><th>Last change</th><th /></tr></thead>
                            <tbody>
                                {(data.branches || []).map((b) => (
                                    <tr key={b.storeId}>
                                        <td><strong>{b.name}</strong>{!b.configured && <small>Not set up yet</small>}</td>
                                        <td>
                                            {b.changedKeys.length ? <b>{b.changedKeys.length}</b> : '—'}
                                            {b.changedKeys.length > 0 && <small>{b.changedKeys.slice(0, 4).map((k) => fieldMap[k]?.label || k).join(', ')}{b.changedKeys.length > 4 ? '...' : ''}</small>}
                                        </td>
                                        <td>{b.ignoredByLock.length || '—'}</td>
                                        <td>{b.lastChange ? <small>{b.lastChange.label}, {fmtTime(b.lastChange.at)}</small> : '—'}</td>
                                        <td className={styles.rowActions}>
                                            <button type="button" className={styles.btnSmall} onClick={() => navigate(`/settings?branch=${b.storeId}`)}>Open</button>
                                            {b.changedKeys.length > 0 && <button type="button" className={styles.btnSmall} onClick={() => resetBranch(b)} disabled={saving}><RotateCcw size={11} /> Reset to HQ</button>}
                                        </td>
                                    </tr>
                                ))}
                                {!data.branches?.length && <tr><td colSpan={5}>No branches yet.</td></tr>}
                            </tbody>
                        </table>
                    </section>
                </div>
            )}

            {tab === 'history' && (
                <div className={styles.pad}>
                    <section className={styles.card}>
                        {!data.changeLog?.length && <p className={styles.muted}>No changes yet.</p>}
                        {(data.changeLog || []).map((c, i) => (
                            <div key={i} className={styles.logRow}>
                                <div><strong>{c.label}</strong>{c.kind === 'LOCK' && <span className={styles.bLock}>Lock</span>}{(c.source === 'HQ' || c.source === 'HQ_RESET') && <span className={styles.bHq}>{c.source === 'HQ_RESET' ? 'Reset by HQ' : 'By HQ'}</span>}</div>
                                {(c.from || c.to) && <div className={styles.logChange}><span>{c.from || 'Empty'}</span> → <b>{c.to || 'Empty'}</b></div>}
                                <small>{c.byName || 'Someone'}{c.byRole ? ` (${c.byRole})` : ''}, {fmtTime(c.at)}</small>
                            </div>
                        ))}
                    </section>
                </div>
            )}

            {dirtyCount > 0 && tab === 'settings' && (
                <div className={styles.saveBar}>
                    <span><b>{dirtyCount}</b> unsaved change{dirtyCount === 1 ? '' : 's'}{onWa ? ' in other settings' : ''}</span>
                    <button type="button" className={styles.btn} onClick={discard} disabled={saving}>Discard</button>
                    <button type="button" className={styles.btnPrimary} onClick={onSave} disabled={saving}>
                        {saving ? <Loader2 size={13} className={styles.spin} /> : <Save size={13} />} {mode === 'HQ' ? 'Save for all branches' : 'Save'}
                    </button>
                </div>
            )}

            {confirm && (
                <div className={styles.overlay} role="presentation" onClick={() => !saving && setConfirm(null)}>
                    <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Confirm HQ changes" onClick={(e) => e.stopPropagation()}>
                        <h3>Save brand settings</h3>
                        <ul className={styles.summary}>
                            {Object.keys(changes).length > 0 && <li><b>{Object.keys(changes).length}</b> value(s) changed</li>}
                            {policyChanges.length > 0 && <li><b>{policyChanges.filter(([, v]) => v === 'LOCKED').length}</b> locked, <b>{policyChanges.filter(([, v]) => v !== 'LOCKED').length}</b> unlocked</li>}
                            <li>Applied to every branch right away. Locked settings replace branch values.</li>
                        </ul>
                        {confirm.affected.length > 0 && (
                            <div className={styles.choice}>
                                <p><b>{confirm.affected.length} branch(es)</b> changed some of these settings themselves ({confirm.affected.map((b) => b.name).join(', ')}).</p>
                                <label><input type="radio" checked={!confirm.reset} onChange={() => setConfirm({ ...confirm, reset: false })} /> Keep their own values (they will not follow this change)</label>
                                <label><input type="radio" checked={confirm.reset} onChange={() => setConfirm({ ...confirm, reset: true })} /> Bring them back to the HQ value</label>
                            </div>
                        )}
                        <div className={styles.modalActions}>
                            <button type="button" className={styles.btn} onClick={() => setConfirm(null)} disabled={saving}>Cancel</button>
                            <button type="button" className={styles.btnPrimary} onClick={confirmHqSave} disabled={saving}>{saving && <Loader2 size={13} className={styles.spin} />} Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
